import { useCallback } from 'react';

import { usePico } from '../pico';

export type ShowcasePageDisplayedProps = {
  showcaseId: string;
  showcaseOwnerId: string;
  showcaseVideoCount: number;
  /** Raw `album.layout` — `'grid' | 'player' | 'live' | 'playlist'`. */
  showcasePageLayout: string;
  showcasePagePrivacy: string;
  showcasePageViewerAuthStatus: string;
  referrerPage: string;
};

export type ShowcaseStartWatchingClickedProps = {
  showcaseId: string;
};

export type ShowcaseVideoClickedProps = {
  showcaseId: string;
  clipId: string;
  showcaseVideoPosition: number;
  showcasePageNumber: number;
};

export type ShowcaseLinkCopiedProps = {
  showcaseId: string;
};

export const useShowcaseTracking = () => {
  const pico = usePico();

  const trackShowcasePageDisplayed = useCallback(
    (props: ShowcasePageDisplayedProps) => {
      if (pico === null) {
        return false;
      }
      // `playlist` is a legacy view that still appears for albums whose
      // effective layout is "player" — collapse it for analytics.
      const layout = props.showcasePageLayout === 'playlist' ? 'player' : props.showcasePageLayout;
      pico.track('showcase_page_displayed', {
        showcase_id: props.showcaseId,
        showcase_owner_id: props.showcaseOwnerId,
        showcase_video_count: props.showcaseVideoCount,
        showcase_page_layout: layout,
        showcase_page_privacy: props.showcasePagePrivacy,
        showcase_page_viewer_auth_status: props.showcasePageViewerAuthStatus,
        referrer_page: props.referrerPage,
      });
      return true;
    },
    [pico],
  );

  const trackShowcaseStartWatchingClicked = useCallback(
    (props: ShowcaseStartWatchingClickedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('showcase_start_watching_clicked', {
        showcase_id: props.showcaseId,
      });
    },
    [pico],
  );

  const trackShowcaseVideoClicked = useCallback(
    (props: ShowcaseVideoClickedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('showcase_video_clicked', {
        showcase_id: props.showcaseId,
        clip_id: props.clipId,
        showcase_video_position: props.showcaseVideoPosition,
        showcase_page_number: props.showcasePageNumber,
      });
    },
    [pico],
  );

  const trackShowcaseLinkCopied = useCallback(
    (props: ShowcaseLinkCopiedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('showcase_link_copied', {
        showcase_id: props.showcaseId,
      });
    },
    [pico],
  );

  return {
    trackShowcasePageDisplayed,
    trackShowcaseStartWatchingClicked,
    trackShowcaseVideoClicked,
    trackShowcaseLinkCopied,
  };
};
